<template>
  <div class="relative w-full">
  <div class="flex items-center border border-[#385660] text-[#385660] py-2 px-4 rounded-xl shadow gap-1.5 w-full"
    :class="{ 'border-red-500': searchError }">
    <div class="relative inline-block">
      <div @click="toggleSuministro" activar="active" class="flex items-center gap-2 cursor-pointer">
        {{ selectedFilterName.label }}
        <ArrowDown class="w-4 inline-block pt-0.5" :class="{ 'rotate-180': showFilter }" />
      </div>

      <transition name="fade">
        <div v-if="showFilter"
          class="absolute right-0 mt-4 min-w-max text-center rounded-lg shadow-lg border-t-[#7CADB9] z-100 bg-white">
          <ul>
            <li v-for="format in selectFilterName" :key="format.key" @click="selectOption(format)"
              class="px-4 py-2 text-[#385660] hover:text-[#59E3FF] cursor-pointer gap-2 whitespace-nowrap">
              <span class="capitalize">{{ format.label }}</span>
            </li>
          </ul>
        </div>
      </transition>
    </div>

    <div class="flex w-full flex-row border-l pl-1.5">
      <input class="bg-transparent flex-1 text-[#385660] outline-none" type="text" v-model="searchText"
        :placeholder="placeholder" @input="handleSearch" :readonly="readonly" />
      <component v-if="icon" :is="icon" class="icon cursor-pointer" />
    </div>
  </div>

  <!-- Hasta ahora un fallo de búsqueda solo se escribía en la consola: la tabla
       se quedaba igual y parecía que el buscador no hacía nada. -->
  <p v-if="searchError" class="absolute top-full left-0 mt-1 text-xs text-red-600 whitespace-normal">
    {{ searchError }}
  </p>
  </div>
</template>

<script setup>
import { defineExpose, defineProps, ref, defineEmits, onUnmounted, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import ArrowDown from '@/assets/svg/ArrowDown.vue';
import { useRoute } from 'vue-router';

const props = defineProps({
  selectFilterName: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: "Buscar..."
  },
  icon: {
    type: Object,
    required: false
  },
  searchEndpoint: {
    type: String,
    required: true
  },
  perPage: {
    type: Number,
    default: 10
  },
  debounceTime: {
    type: Number,
    default: 500
  },
  type: {
    type: String,
    default: null
  },
  supply_type: {
    type: String,
    default: 'both'
  },
  user_id: {
    type: Number,
    default: null
  },
  property_type: {
    type: String,
    default: null
  },
  readonly: {
    type: Boolean,
    default: false
  },
  // Parámetros propios de la tabla que monta el buscador (por ejemplo el
  // filtro de roles, para que la búsqueda cubra el mismo universo que el
  // listado). Se envían tal cual junto al resto de la query.
  extraParams: {
    type: Object,
    default: () => ({})
  }
});

defineExpose({ performSearch });
const searchPrimaryRef = ref(null);

const { t } = useI18n();
const route = useRoute();
const showFilter = ref(false);
const searchText = ref('');
const selectedFilterName = ref(props.selectFilterName[0] || { label: 'Seleccionar' });
let searchTimeout = null;
let lastController = null;

const results = ref([]);
const searchError = ref('');

// Inicializar el texto de búsqueda y el filtro con el estado
onMounted(() => {
  if (route.query.status) {
    // Buscar el filtro de status en los selectFilterName
    const statusFilter = props.selectFilterName.find(filter => filter.key === 'status');
    if (statusFilter) {
      selectedFilterName.value = statusFilter;
      searchText.value = route.query.status;
      handleSearch();
    }
  }
});

watch(
  () => props.selectFilterName,
  (options) => {
    if (!options || options.length === 0) return;
    const stillThere = options.some((option) => option.key === selectedFilterName.value?.key);
    if (!stillThere) {
      selectedFilterName.value = options[0];
    } else {
      // Mantiene la etiqueta al día cuando cambia el idioma.
      selectedFilterName.value = options.find((option) => option.key === selectedFilterName.value.key);
    }
  },
  { deep: true }
);

const emit = defineEmits([
  'update:selectedFilter',
  'search-results',
  'search-error',
  'reset-search',
  'update-page',
]);

function toggleSuministro() {
  showFilter.value = !showFilter.value;
}

function selectOption(option) {
  selectedFilterName.value = option;
  showFilter.value = false;
  emit('update:selectedFilter', option);
  handleSearch();
}

async function performSearch(page = 1) {
  try {
    // Cancelar petición anterior si existe
    if (lastController) {
      lastController.abort();
    }
    lastController = new AbortController();
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}${props.searchEndpoint}`,
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          query: searchText.value,
          per_page: props.perPage,
          page: page,
          search_by: selectedFilterName.value.key,
          type: props.type,
          status: route.query.status,
          supply_type: props.supply_type,
          user_id: props.user_id,
          property_type: props.property_type,
          ...props.extraParams
        },
        signal: lastController.signal
      }
    );
    searchError.value = '';
    // Si la API responde sin resultados, emitir array vacío
    let data = response.data;
    if (!data || !Array.isArray(data.data) || data.data.length === 0) {
      data = { ...data, data: [] };
    }
    results.value = data;
    results.value.query = searchText.value;
    results.value.path = props.searchEndpoint;
    emit('search-results', results.value);
  } catch (error) {
    if (axios.isCancel?.(error) || error.name === 'CanceledError' || error.message === 'canceled') {
      // Petición cancelada, no hacer nada
      return;
    }
    searchError.value = errorMessageFor(error);
    console.error('Error en la búsqueda:', error);
    emit('search-error', error);
  }
}

/**
 * Traduce el fallo a algo que el usuario pueda entender. Un 422 significa que
 * la API no admite ese filtro; el resto se resume como fallo de búsqueda.
 */
function errorMessageFor(error) {
  const status = error?.response?.status;

  if (status === 422) {
    return t('search.unsupportedFilter', { filter: selectedFilterName.value?.label ?? '' });
  }
  if (status === 401 || status === 403) {
    return t('search.forbidden');
  }

  return t('search.failed');
}

function handleSearch() {
  if (searchTimeout) clearTimeout(searchTimeout);

  searchTimeout = setTimeout(() => {
    if (searchText.value.length >= 1) {
      // Notify parent to update to page 1; parent will call `performSearch`
      // to avoid duplicate requests (child should not call performSearch
      // directly when emitting page changes).
      emit('update-page', 1);
    } else {
      searchError.value = '';
      emit('reset-search');
    }
  }, props.debounceTime);
}

// Limpiar el timeout cuando el componente se desmonte
onUnmounted(() => {
  console.log('Componente desmontado', props.selectFilterName);
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  if (lastController) {
    try { lastController.abort(); } catch(e) {}
    lastController = null;
  }
});
</script>

<style scoped>
input {
  background: transparent;
  border: #385660;
  outline: none;
  color: #385660;
}

input::placeholder {
  color: #385660;
}
</style>
